import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, Box, Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./OfferDelete.css";

export default function OfferDeleteDialog({
  open,
  onClose,
  record,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const rawRecord = useMemo(() => record ?? null, [record]);

  /*
    La deschiderea dialogului:
    - resetăm starea de loading
    - ștergem eventualele erori vechi
   */
  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  /*
    Funcția care face efectiv ștergerea ofertei.
    Dacă merge bine, apelăm callback-ul onDeleted.
    Dacă nu merge, afișăm un mesaj de eroare clar.
   */
  const handleDelete = async () => {
    if (!rawRecord?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/offer-register/${rawRecord.id}/`);
      onDeleted?.();
    } catch (e) {
      setError(
        "Cannot delete this offer because it has dependencies in other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="offer-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header-ul apare doar cât timp nu avem eroare */}
      {!error && (
        <DialogTitle className="od-header">
          <Box className="od-header-content">
            <Box className="od-title-section">
              <WarningAmberIcon className="od-warning-icon" />
              <Typography variant="h5" component="span" className="od-title">
                Delete Offer
              </Typography>
            </Box>

            {/* Butonul de închidere */}
            <IconButton
              onClick={onClose}
              className="od-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="od-content">
        {error ? (
          // Dacă ștergerea a eșuat, afișăm un mesaj clar de eroare.
          <Alert
            severity="error"
            className="od-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Offer
            </Typography>
            <Typography variant="body2" className="od-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          // Dacă nu există eroare, afișăm mesajul de confirmareși informațiile despre oferta care urmează să fie ștearsă.
          <Box className="od-confirm-content">
            {/* Dacă există codul ofertei, îl afișăm ca să fie clar ce se șterge */}
            {rawRecord?.offer_code && (
              <Box className="od-code-container">
                <Typography variant="subtitle2" className="od-code-label">
                  Offer Number
                </Typography>
                <Typography variant="h6" className="od-code-value">
                  {rawRecord.offer_code}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" className="od-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="od-consequences">
              <Typography variant="body2" className="od-consequence-item">
                • The offer will be permanently deleted from the system
              </Typography>
              <Typography variant="body2" className="od-consequence-item">
                • All associated offer data will be removed
              </Typography>
              <Typography variant="body2" className="od-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="od-actions">
        {error ? (
          // Dacă a apărut eroare, utilizatorul primește doar un buton simplu de închidere.
          <Button
            onClick={onClose}
            variant="contained"
            className="od-btn od-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            {/* Butonul Cancel */}
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="od-btn od-btn-cancel"
            >
              Cancel
            </Button>

            {/* Butonul Delete */}
            <Button
              onClick={handleDelete}
              disabled={deleting || !rawRecord?.id}
              variant="contained"
              className="od-btn od-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}